import {
    BaseButton,
    ClaimsBundleBadge,
    DataTable,
    DataTablePresenter,
    ITableColumn,
    TimelineSegment,
} from '@enter/core';
import { mdiChevronDown, mdiChevronRight } from '@mdi/js';
import _ from 'lodash';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CellProps, Row, UseExpandedRowProps } from 'react-table';
import { formatDate, formatDistanceToNow } from '../../../services/primitives/date-utils';

// Extend Row type to include expansion methods
type ExpandedRow<T extends object> = Row<T> & UseExpandedRowProps<T>;
import { FilterSelect } from './filter-select';
import * as S from './claim-timeline.styles';
import { IClaimTimelineProps, ITimelineEvent } from './claim-timeline.types';
import { TimelineRedesignList } from './timeline-redesign-list';
import {
    assignClaimDataNumbers,
    canTimelineRowToggleExpansion,
    getTimelineEventLabel,
    getTimelineFilterKey,
    isClaimDataTimelineEvent,
    isMessageTimelineEvent,
} from './timeline-utils';
import { hasGenericTimelineEventDetail, resolveTimelineExpandContent } from './timeline-event-detail';

/**
 * Unified ClaimTimeline component
 * Consolidates Provider's claim-timeline-table and Support's timeline components
 *
 * Uses Support's architecture:
 * - Accepts unified resources array
 * - Apps provide getItem mapper to convert resources to timeline events
 * - Core handles display and interaction logic only
 *
 * Wrapped in `observer` so Support can pass `supportClaimsStore` and reactive fields like
 * `inactivePayments.data` re-render the timeline the same way legacy `Timelime` did on master.
 */
function ClaimTimelineComponent(props: IClaimTimelineProps): React.ReactElement {
    const {
        appType,
        loading,
        resources,
        claim,
        claimBundle,
        loadingBundle,
        onLoadClaimBundle,
        getItem,
        dialogContext,
        messagingContext,
        messageStore,
        onChange,
        renderActions,
        renderTags,
        title,
        caption,
        info,
        showTitleCard = true,
        layoutVariant = 'redesigned',
        supportClaimsStore,
        loadInactivePayments,
        hasLoadedInactivePayments = false,
        inactivePaymentResources = [],
    } = props;

    const loadInactivePaymentsFn = supportClaimsStore?.loadInactivePayments ?? loadInactivePayments;
    const hasLoadedInactivePaymentsResolved =
        supportClaimsStore?.hasLoadedInactivePayments ?? hasLoadedInactivePayments;

    // Same mapping as enter-support `mapClaimResource(_, 'PAYMENT')` — keep inactive merge self-contained.
    const inactivePaymentResourcesForMerge = useMemo(() => {
        if (supportClaimsStore) {
            return supportClaimsStore.inactivePayments.data.map((el: any) => ({
                ...el,
                resourceType: 'PAYMENT',
            }));
        }
        return inactivePaymentResources;
    }, [supportClaimsStore, supportClaimsStore?.inactivePayments.data, inactivePaymentResources]);

    const [expandedIds, setExpandedIds] = useState<string[]>([]);
    const [expandedDetailIds, setExpandedDetailIds] = useState<string[]>([]);
    const [hiddenEvents, setHiddenEvents] = useState<string[]>([]);
    const [isBundleExpanded, setIsBundleExpanded] = useState(false);
    const [bundleClaims, setBundleClaims] = useState<any[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const isLoadingCategoriesRef = useRef(false);
    const [showInactivePayments, setShowInactivePayments] = useState(false);
    const [loadingInactivePayments, setLoadingInactivePayments] = useState(false);

    const effectiveResources = useMemo(() => {
        const base = resources;
        if (appType !== 'support' || !showInactivePayments || inactivePaymentResourcesForMerge.length === 0) {
            return base;
        }
        return _.orderBy(
            [...base, ...inactivePaymentResourcesForMerge],
            [(resource: any) => (resource.resourceType === 'MESSAGE' ? resource.updated : resource.created)],
            ['desc'],
        );
    }, [appType, resources, showInactivePayments, inactivePaymentResourcesForMerge]);

    // Check if there are any messages with categories (Support pattern)
    const hasMessagesWithCategories = useMemo(() => {
        return resources.some(
            (resource: any) =>
                resource.resourceType === 'MESSAGE' &&
                resource.links?.messageCategories &&
                resource.links.messageCategories.length > 0,
        );
    }, [resources]);

    // Access messageCategories to ensure MobX tracks it for reactivity
    const messageCategories = messageStore.messageCategories;

    // Check if we need to load categories
    const needsCategoryLoad = hasMessagesWithCategories && messageCategories.length === 0;

    // Load message categories if needed (Support pattern)
    useEffect(() => {
        if (needsCategoryLoad && !isLoadingCategoriesRef.current && messageStore.loadMessageCategories) {
            isLoadingCategoriesRef.current = true;
            setCategoriesLoading(true);
            messageStore.loadMessageCategories().finally(() => {
                isLoadingCategoriesRef.current = false;
                setCategoriesLoading(false);
            });
        } else if (!needsCategoryLoad) {
            isLoadingCategoriesRef.current = false;
        }
    }, [needsCategoryLoad, messageStore]);

    // Combine loading states (initial / categories / bundle — hides filter row until ready)
    const isLoading = loading || (needsCategoryLoad && categoriesLoading) || loadingBundle;
    // Table also shows loading while fetching inactive payments; filter row stays visible
    const tableIsLoading = isLoading || loadingInactivePayments;

    // Convert resources to timeline events using provided mapper
    const mappedItems: ITimelineEvent[] = useMemo(
        () => effectiveResources.map(resource => getItem(resource)),
        [effectiveResources, getItem],
    );
    const items = useMemo(
        () => deduplicateConsecutiveCodingItems(mappedItems, expandedIds),
        [mappedItems, expandedIds],
    );

    // Filter hidden events
    let data = useMemo(() => {
        return items.filter(item => {
            const eventType = getTimelineFilterKey(item);
            return eventType && !hiddenEvents.includes(eventType);
        });
    }, [items, hiddenEvents]);

    // Group similar events
    data = useMemo(() => assignClaimDataNumbers(getGroupedItems(data, expandedIds)), [data, expandedIds]);

    // Provider bundle handling
    useEffect(() => {
        const fetchBundleData = async () => {
            const claimObj = claim as any;
            const bundleId = claimObj?.claimBundleId;
            if (bundleId && claimBundle) {
                const claimData = claimBundle
                    .filter(el => (el as any).id !== claimObj?.id)
                    .map(el => ({
                        id: (el as any).id!,
                        chargeAmount: (el as any).chargeAmount,
                        fromDos: (el as any).fromDos,
                    }));
                setBundleClaims(claimData);
            }
        };
        fetchBundleData();
    }, [claimBundle, claim]);

    const handleToggleBundle = () => {
        if (!isBundleExpanded && bundleClaims.length === 0) {
            const claimObj = claim as any;
            const bundleId = claimObj?.claimBundleId;
            if (bundleId && onLoadClaimBundle) {
                onLoadClaimBundle(bundleId);
            }
        }
        setIsBundleExpanded(!isBundleExpanded);
    };

    // Define columns based on appType
    const columns: ITableColumn<ITimelineEvent>[] = useMemo(() => {
        const cols: ITableColumn<ITimelineEvent>[] = [
            // Segment column (both apps)
            {
                id: 'segment',
                minWidth: 12,
                maxWidth: 12,
                Cell: ({ row }: CellProps<ITimelineEvent>): React.ReactElement => {
                    const isSelectable = !!row.original.details || !!row.original.onClick;
                    return (
                        <S.StyledSegment selectable={isSelectable}>
                            <TimelineSegment start={row.index === 0} />
                        </S.StyledSegment>
                    );
                },
                disableSortBy: true,
            },
            // Date Created column (both apps)
            {
                id: 'created',
                Header: 'Date Created',
                accessor: 'created',
                maxWidth: 200,
                width: 200,
                Cell: ({ cell }: CellProps<ITimelineEvent>): string => {
                    if (!cell.value) {
                        return '';
                    }
                    return formatDate(cell.value, 'MMM dd, yyyy hh:mm a');
                },
                disableSortBy: true,
            },
        ];

        // Event column (unified rendering with icons for both apps)
        cols.push({
            id: 'event',
            Header: 'Event',
            accessor: (row: ITimelineEvent) => getTimelineEventLabel(row),
            maxWidth: 200,
            width: 200,
            Cell: ({ row }: CellProps<ITimelineEvent>) => {
                const label = getTimelineEventLabel(row.original);
                return (
                    <>
                        {row.original.icon ? (
                            <S.Event>
                                <S.EventIcon path={row.original.icon} size={0.75} />
                                <S.EventLabel>{label}</S.EventLabel>
                            </S.Event>
                        ) : (
                            <S.SelectableCell selectable={!!row.original.details || !!row.original.onClick}>
                                <S.StyledEvent>{label}</S.StyledEvent>
                            </S.SelectableCell>
                        )}
                        {!!row.original.secondaryType && (
                            <S.SecondaryEvent>{row.original.secondaryType}</S.SecondaryEvent>
                        )}
                    </>
                );
            },
            disableSortBy: true,
        });

        // Description column (both apps)
        cols.push({
            id: 'description',
            Header: 'Description',
            Cell: ({ row }: CellProps<ITimelineEvent>): React.ReactNode => {
                const isSelectable = !!row.original.details || !!row.original.onClick;
                return appType === 'provider' ? (
                    <S.StyledDescription selectable={isSelectable}>
                        <S.Description>{row.original.description}</S.Description>
                    </S.StyledDescription>
                ) : (
                    row.original.description
                );
            },
            minWidth: 400,
            maxWidth: appType === 'provider' ? 700 : 600,
            disableSortBy: true,
        });

        // Type column (Provider only)
        if (appType === 'provider') {
            cols.push({
                id: 'type',
                Header: 'Type',
                accessor: 'type',
                Cell: ({ cell }: CellProps<ITimelineEvent>): React.ReactElement => (
                    <S.SelectableCell selectable={!!cell.row.original.details || !!cell.row.original.onClick}>
                        {cell.value}
                    </S.SelectableCell>
                ),
                maxWidth: 200,
                minWidth: 170,
                disableSortBy: true,
            });
        }

        // Tags column (Support only)
        if (appType === 'support' && renderTags) {
            cols.push({
                id: 'tags',
                Header: 'Tags',
                accessor: 'tag',
                maxWidth: 250,
                minWidth: 200,
                Cell: ({ cell }: CellProps<ITimelineEvent>) => {
                    return cell.value ? renderTags(cell.value) : null;
                },
                disableSortBy: true,
            });
        }

        // Actions column (Support only)
        if (appType === 'support' && onChange && renderActions) {
            cols.push({
                id: 'Actions',
                Header: '',
                accessor: 'actions',
                maxWidth: 100,
                Cell: ({ row }: CellProps<ITimelineEvent>) => {
                    return renderActions(row.original.actions, onChange);
                },
                disableSortBy: true,
            });
        }

        // Selectable/Expand column (both apps)
        cols.push({
            id: 'selectable',
            Cell: ({ row }: CellProps<ITimelineEvent>) => {
                if (!row.original.details && !row.original.onClick) {
                    return null;
                }

                const expandedRow = row as ExpandedRow<ITimelineEvent>;
                const path = expandedRow.isExpanded ? mdiChevronDown : mdiChevronRight;
                return (
                    <S.SelectableCell selectable={true}>
                        {appType === 'provider' ? <S.StyledIcon path={path} /> : <S.ExpandIcon path={path} size={1} />}
                    </S.SelectableCell>
                );
            },
            minWidth: appType === 'provider' ? 50 : 70,
            maxWidth: appType === 'provider' ? 50 : 70,
            disableSortBy: true,
        });

        return cols;
    }, [data, appType, onChange]);

    const { current: presenter } = useRef(
        new DataTablePresenter<ITimelineEvent>({
            name: appType === 'provider' ? 'provider-timeline' : 'support-timeline',
            data,
            columns,
            filterSchemas: [],
        }),
    );

    useEffect(() => {
        presenter.onColumnsUpdate(columns);
    }, [presenter, columns]);

    useEffect(() => {
        presenter.onDataUpdate(data);
        presenter.onDataTotalChange(data.length);
    }, [presenter, data]);

    // DataTable always paginates (default page size 20). Support hides pagination UI, and Provider
    // hides navigation, so rows beyond the first page are unreachable. Size the page to the full
    // timeline so merged inactive payments (often older / later in sort order) stay visible.
    useEffect(() => {
        const len = data.length;
        if (len === 0) {
            return;
        }
        if (presenter.controlledPageSize < len) {
            presenter.onPageSizeUpdate(len);
        }
    }, [presenter, data.length]);

    // Event type list for filtering
    const eventTypes = useMemo(() => {
        return _(items)
            .map(el => getTimelineFilterKey(el))
            .uniq()
            .sort()
            .compact()
            .value();
    }, [items]);

    const handleChangeHiddenFilters = (filters: string[]) => {
        setHiddenEvents(filters);
    };

    const handleToggleInactivePayments = async () => {
        const claimId = (claim as { id?: string })?.id;
        if (!claimId || !loadInactivePaymentsFn) {
            return;
        }

        if (!showInactivePayments && !hasLoadedInactivePaymentsResolved) {
            setLoadingInactivePayments(true);
            try {
                await loadInactivePaymentsFn(claimId);
            } finally {
                setLoadingInactivePayments(false);
            }
            setShowInactivePayments(true);
            return;
        }

        setShowInactivePayments(prev => !prev);
    };

    // Row click handler (legacy DataTable)
    const onRowClick = async (row: Row<ITimelineEvent>) => {
        const expandedRow = row as ExpandedRow<ITimelineEvent>;
        const item = row.original;
        const canToggleExpansion = canTimelineRowToggleExpansion(item);
        const willExpand = canToggleExpansion && !expandedRow.isExpanded;

        if (item.onClick && willExpand) {
            try {
                await item.onClick();
            } catch (err) {
                if (dialogContext.showError) {
                    dialogContext.showError(err);
                } else {
                    console.error('Timeline row click error:', err);
                }
            }
        }

        if (item.details || (isMessageTimelineEvent(item) && item.onClick)) {
            expandedRow.toggleRowExpanded();
        }
    };

    const handleRedesignItemClick = async (item: ITimelineEvent) => {
        const canToggleExpansion = canTimelineRowToggleExpansion(item) && !!item.id;
        const isExpanded = !!item.id && expandedDetailIds.includes(item.id);
        const willExpand = canToggleExpansion && !isExpanded;

        if (item.onClick && willExpand) {
            try {
                await item.onClick();
            } catch (err) {
                if (dialogContext.showError) {
                    dialogContext.showError(err);
                } else {
                    console.error('Timeline row click error:', err);
                }
            }
        }

        if (canToggleExpansion) {
            setExpandedDetailIds(prev =>
                isExpanded ? prev.filter(id => id !== item.id) : [...prev, item.id!],
            );
        }
    };

    const handleExpandGrouped = (item: ITimelineEvent) => {
        if (item.id) {
            setExpandedIds(_.uniq([...expandedIds, item.id]));
        }
    };

    // Render expanded row content
    const renderRowSubComponent = (row: Row<ITimelineEvent>) => {
        const details = resolveTimelineExpandContent(row.original);
        if (!details) {
            return null;
        }

        const isLast = row.index === _.size(data) - 1;

        return appType === 'provider' ? (
            <S.ExpandRow>
                {isLast && <S.ExpandSegment />}
                {!isLast && (
                    <S.ExpandSegment>
                        <TimelineSegment />
                    </S.ExpandSegment>
                )}
                <S.ExpandCell>{details}</S.ExpandCell>
                <S.ExpandSegment />
            </S.ExpandRow>
        ) : (
            <S.SubRowContainer>
                <S.SegmentContainer>
                    <TimelineSegment />
                </S.SegmentContainer>
                {details}
            </S.SubRowContainer>
        );
    };

    // Render row wrapper with grouped items indicator
    const renderRowWrapper = (row: Row<ITimelineEvent>, renderRow: React.ReactElement) => {
        const groupedItems = row.original.groupedItems || [];
        const handleExpandEvents = () => {
            if (row.original.id) {
                setExpandedIds(_.uniq([...expandedIds, row.original.id]));
            }
        };

        return (
            <S.RowWrapper>
                {renderRow}
                {!_.isEmpty(groupedItems) && (
                    <S.RowGroupWrapper onClick={handleExpandEvents}>
                        {(row.original.event || row.original.type) === 'Coding'
                            ? `There are ${groupedItems.length} duplicate coding events. Click to show.`
                            : `There are ${groupedItems.length} more similar events. Click to show.`}
                    </S.RowGroupWrapper>
                )}
            </S.RowWrapper>
        );
    };

    // Render caption (Provider only)
    const renderCaption = (): React.ReactNode | undefined => {
        if (caption) {
            return caption;
        }
        const recent = _.first(data);
        const timeSince = recent?.created ? formatDistanceToNow(new Date(recent.created)) : undefined;
        if (timeSince) {
            return `Last event: ${timeSince} ago`;
        }
    };

    const content = (
        <>
            {/* Provider bundle feature */}
            {appType === 'provider' && (claim as any)?.claimBundleId && (
                <S.ClaimBundleWrapper>
                    <ClaimsBundleBadge
                        bundleId={(claim as any).claimBundleId}
                        claims={bundleClaims}
                        isExpanded={isBundleExpanded}
                        onToggle={handleToggleBundle}
                    />
                </S.ClaimBundleWrapper>
            )}

            {/* Filter + support inactive payments toggle */}
            {!isLoading &&
                (appType === 'support' && loadInactivePaymentsFn ? (
                    <S.PaymentsToggleRow>
                        <FilterSelect filters={eventTypes} onChangeHiddenFilters={handleChangeHiddenFilters} />
                        <BaseButton
                            variant="outlined"
                            size="small"
                            type="button"
                            disabled={loadingInactivePayments}
                            onClick={handleToggleInactivePayments}>
                            {loadingInactivePayments
                                ? 'Loading inactive payments...'
                                : showInactivePayments
                                  ? 'Hide inactive payments'
                                  : 'Show inactive payments'}
                        </BaseButton>
                    </S.PaymentsToggleRow>
                ) : (
                    <FilterSelect filters={eventTypes} onChangeHiddenFilters={handleChangeHiddenFilters} />
                ))}

            {/* Timeline rows */}
            {layoutVariant === 'redesigned' ? (
                <TimelineRedesignList
                    items={data}
                    appType={appType}
                    isLoading={tableIsLoading}
                    expandedDetailIds={expandedDetailIds}
                    onItemClick={handleRedesignItemClick}
                    onExpandGrouped={handleExpandGrouped}
                    renderTags={renderTags}
                    renderActions={renderActions}
                    onChange={onChange}
                />
            ) : (
                <S.StyledDataTable
                    isLoading={tableIsLoading}
                    presenter={presenter}
                    hideNavigation={true}
                    hidePagination={true}
                    hideFiltering={true}
                    hideSettings={true}
                    disableSorting={true}
                    onRowClick={onRowClick}
                    renderRowWrapper={renderRowWrapper}
                    renderRowSubComponent={renderRowSubComponent}
                />
            )}
        </>
    );

    // Wrap in shared TitleCard when enabled
    if (showTitleCard) {
        return (
            <S.StyledTitleCard size="large" info={info} caption={renderCaption()} title={title || 'Claim Timeline'}>
                {content}
            </S.StyledTitleCard>
        );
    }

    return <S.TableWrapper>{content}</S.TableWrapper>;
}

export const ClaimTimeline = observer(ClaimTimelineComponent);

/**
 * Group similar consecutive events together
 * Extracted from both implementations (identical logic)
 */
function getGroupedItems(items: ITimelineEvent[], expandedIds: string[]): ITimelineEvent[] {
    const res: ITimelineEvent[] = [];
    let temp: ITimelineEvent[] = [];

    function addTemp() {
        const firstItem = _.first(temp);
        if (!firstItem?.id || expandedIds.includes(firstItem.id)) {
            // Clear groupedItems when expanded to hide the badge
            if (firstItem) {
                firstItem.groupedItems = undefined;
            }
            res.push(...temp);
            return;
        }

        if ((firstItem.event || firstItem.type) === 'Coding') {
            res.push(...temp);
            return;
        }

        if (temp.length > 2) {
            firstItem.groupedItems = [...temp.slice(1)];
            res.push(firstItem);
        } else {
            res.push(...temp);
        }
    }

    for (const item of items) {
        const lastItem = _.last(temp);

        // when temp array is empty
        if (!lastItem) {
            temp.push(item);
            continue;
        }

        // Claim data snapshots are distinct — never collapse consecutive rows.
        if (isClaimDataTimelineEvent(item) || isClaimDataTimelineEvent(lastItem)) {
            addTemp();
            temp = [item];
            continue;
        }

        // build temp array, group the similar events
        const itemType = getTimelineFilterKey(item);
        const lastType = getTimelineFilterKey(lastItem);

        if (itemType && itemType === lastType) {
            temp.push(item);
        } else {
            addTemp();
            temp = [item];
        }
    }

    // add the rest
    addTemp();

    return res;
}

function deduplicateConsecutiveCodingItems(items: ITimelineEvent[], expandedIds: string[]): ITimelineEvent[] {
    const normalizedItems = items.map(item => ({
        ...item,
        groupedItems: undefined,
    }));
    const deduplicated: ITimelineEvent[] = [];

    for (const item of normalizedItems) {
        if ((item.event || item.type) !== 'Coding' || !item.codingSignature) {
            deduplicated.push(item);
            continue;
        }

        if (item.isMessageLinkedCoding) {
            deduplicated.push(item);
            continue;
        }

        const previousKeptItem = _.last(deduplicated);
        const isDuplicateCoding =
            (previousKeptItem?.event || previousKeptItem?.type) === 'Coding' &&
            !!previousKeptItem.codingSignature &&
            !previousKeptItem.isMessageLinkedCoding &&
            previousKeptItem.codingSignature === item.codingSignature;

        if (!isDuplicateCoding) {
            deduplicated.push(item);
            continue;
        }

        const hasExpandedAnchor = deduplicated.some(
            dedupedItem =>
                (dedupedItem.event || dedupedItem.type) === 'Coding' &&
                dedupedItem.codingSignature === item.codingSignature &&
                !!dedupedItem.id &&
                expandedIds.includes(dedupedItem.id),
        );
        if (hasExpandedAnchor) {
            deduplicated.push(item);
            continue;
        }

        const isExpanded = !!previousKeptItem?.id && expandedIds.includes(previousKeptItem.id);
        if (isExpanded) {
            deduplicated.push(item);
            continue;
        }

        if (previousKeptItem) {
            previousKeptItem.groupedItems = [...(previousKeptItem.groupedItems || []), item];
        }
    }

    return deduplicated;
}
